import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Crown, EnvelopeSimple, Clock, X } from "@phosphor-icons/react";
import {
  requestProjectTransfer,
  getPendingTransfer,
  cancelPendingTransfer,
} from "@/lib/api";

// Confirms the new-owner email and sends the Resend transfer email. The
// invited user accepts via the link in their inbox (see /transfer/accept route).
export default function TransferOwnershipDialog({ open, onOpenChange, project }) {
  const [email, setEmail] = useState("");
  const [confirm, setConfirm] = useState("");
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [pending, setPending] = useState(null);
  const [loadingPending, setLoadingPending] = useState(false);

  useEffect(() => {
    if (!open) return;
    setEmail("");
    setConfirm("");
    setNote("");
    let cancelled = false;
    (async () => {
      setLoadingPending(true);
      try {
        const p = await getPendingTransfer(project.id);
        if (!cancelled) setPending(p || null);
      } catch (_) {
        if (!cancelled) setPending(null);
      } finally {
        if (!cancelled) setLoadingPending(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, project.id]);

  const submit = async (e) => {
    e.preventDefault();
    const e1 = email.trim().toLowerCase();
    const e2 = confirm.trim().toLowerCase();
    if (!e1 || !e1.includes("@")) {
      toast.error("Enter a valid email address");
      return;
    }
    if (e1 !== e2) {
      toast.error("Email addresses don't match");
      return;
    }
    if (e1 === (project.admin_email || "").toLowerCase()) {
      toast.error("That email already owns this project");
      return;
    }
    setSubmitting(true);
    try {
      const res = await requestProjectTransfer(project.id, e1, note);
      if (res?.email_sent) {
        toast.success(`Transfer email sent to ${e1}. They have 7 days to accept.`);
      } else {
        toast.success(`Transfer initiated for ${e1}. (Email queued — we'll retry delivery.)`);
      }
      // Refresh pending state so the dialog now shows the live status banner.
      const p = await getPendingTransfer(project.id);
      setPending(p || null);
      setEmail("");
      setConfirm("");
      setNote("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't initiate transfer");
    } finally {
      setSubmitting(false);
    }
  };

  const cancelTransfer = async () => {
    setSubmitting(true);
    try {
      await cancelPendingTransfer(project.id);
      setPending(null);
      toast.success("Pending transfer cancelled");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't cancel");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-md max-w-lg" data-testid="transfer-ownership-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-black tracking-tight flex items-center gap-2">
            <Crown size={20} weight="fill" className="text-amber-500" />
            Transfer Ownership
          </DialogTitle>
          <DialogDescription className="text-slate-500">
            Pass admin rights for <strong className="text-slate-950">{project.name}</strong> to
            another teammate. They'll receive an email with an accept link.
          </DialogDescription>
        </DialogHeader>

        {pending ? (
          <div
            className="rounded-md border border-amber-300 bg-amber-50 p-4 space-y-3"
            data-testid="transfer-pending-banner"
          >
            <div className="flex items-start gap-3">
              <div className="h-9 w-9 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center shrink-0">
                <Clock size={16} weight="bold" />
              </div>
              <div className="min-w-0">
                <div className="text-sm font-semibold text-amber-900">Pending transfer</div>
                <div className="text-xs text-amber-800 mt-0.5">
                  Waiting on <span className="font-mono-ibm">{pending.new_owner_email}</span> to accept.
                  Expires {new Date(pending.expires_at).toLocaleDateString()}.
                </div>
              </div>
            </div>
            <div className="flex justify-end">
              <Button
                onClick={cancelTransfer}
                disabled={submitting}
                variant="outline"
                size="sm"
                className="rounded-md border-amber-300 text-amber-900 hover:bg-amber-100"
                data-testid="transfer-cancel-pending-btn"
              >
                <X size={12} weight="bold" />
                <span className="ml-1.5">Cancel pending transfer</span>
              </Button>
            </div>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4 py-1">
            <div className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2.5 text-[12px] text-rose-900 leading-relaxed">
              <strong>Heads up.</strong> Once accepted, you'll lose admin rights and become an
              edit-access collaborator. The new owner controls billing seat, distribution list,
              and project deletion.
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-slate-500 font-mono-ibm">
                New owner email
              </Label>
              <div className="relative mt-1.5">
                <EnvelopeSimple
                  size={14}
                  weight="bold"
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                />
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="teammate@company.com"
                  className="h-10 rounded-md border-slate-300 pl-9 focus-visible:ring-0 focus-visible:border-slate-950"
                  data-testid="transfer-email-input"
                  required
                />
              </div>
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-slate-500 font-mono-ibm">
                Confirm email
              </Label>
              <Input
                type="email"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                placeholder="teammate@company.com"
                className="mt-1.5 h-10 rounded-md border-slate-300 focus-visible:ring-0 focus-visible:border-slate-950"
                data-testid="transfer-email-confirm-input"
                required
              />
              {confirm && email && confirm.trim().toLowerCase() !== email.trim().toLowerCase() && (
                <p
                  className="text-[11px] text-rose-600 mt-1"
                  data-testid="transfer-email-mismatch-hint"
                >
                  Emails don't match.
                </p>
              )}
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-slate-500 font-mono-ibm">
                Note <span className="normal-case tracking-normal text-slate-400">(optional)</span>
              </Label>
              <Textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                rows={3}
                placeholder="Hey — I'm handing over the launch plan for Q3…"
                className="mt-1.5 rounded-md border-slate-300 focus-visible:ring-0 focus-visible:border-slate-950 text-sm resize-none"
                data-testid="transfer-note-input"
                maxLength={500}
              />
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                className="rounded-md border-slate-300"
                data-testid="transfer-cancel-btn"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={
                  submitting ||
                  loadingPending ||
                  !email.trim() ||
                  email.trim().toLowerCase() !== confirm.trim().toLowerCase()
                }
                className="rounded-md bg-slate-950 hover:bg-slate-800 text-white"
                data-testid="transfer-submit-btn"
              >
                {submitting ? "Sending…" : "Send transfer email"}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
